import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PortalPage, Section, Field } from "../../components/portal/PortalPage";
import { usePortal, refreshPortalMe } from "../../components/portal/usePortal";
import { TIER_LABEL } from "../../components/portal/portalUtils";
import PWAInstallCard from "../../components/PWAInstallCard";
import { Lock, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientSettingsPage() {
  const { me, user, client } = usePortal();
  const [f, setF] = useState({ name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setF({ name: user.name || "", phone: user.phone || "" }); }, [user]);

  const save = async () => {
    setBusy(true);
    try { await API.put("/client-hub/settings", f); toast.success("Settings saved"); refreshPortalMe(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <PortalPage title="Settings" subtitle="Your profile, notifications and security." testid="client-settings">
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Your Profile" testid="settings-profile">
          <div className="space-y-3">
            <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="settings-name" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="settings-phone" /></Field>
            <Field label="Email"><Input value={user?.email || ""} disabled /></Field>
            <Button onClick={save} disabled={busy} data-testid="settings-save">Save</Button>
          </div>
        </Section>
        <div className="space-y-6">
          <Section title="Notifications" testid="settings-notifications">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <BellRinging size={20} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold">Choose what you're told about</p>
                  <p className="text-[11px] text-muted-foreground">Job updates, quotes, messages, invoices and more — switch each on or off for the portal and for email.</p>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild><Link to="/portal/notification-settings" data-testid="settings-notification-prefs-link">Manage</Link></Button>
            </div>
          </Section>
          <Section title="Organisation" testid="settings-org">
            <dl className="text-xs space-y-2">
              <div className="flex justify-between"><dt className="text-muted-foreground">Company</dt><dd className="font-medium">{client?.display_name}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Your role</dt><dd className="font-medium">{TIER_LABEL[me?.user?.tier] || "User"}</dd></div>
              {client?.email && <div className="flex justify-between"><dt className="text-muted-foreground">Account email</dt><dd className="font-medium">{client.email}</dd></div>}
            </dl>
            <p className="text-[11px] text-muted-foreground mt-3">Need company details changed? Message our team from the Messages page.</p>
          </Section>
          <Section title="Security" testid="settings-security">
            <Button variant="outline" asChild><Link to="/change-password" data-testid="settings-change-password"><Lock size={14} className="mr-1" /> Change password</Link></Button>
          </Section>
          <PWAInstallCard />
        </div>
      </div>
    </PortalPage>
  );
}
